import { useQuery } from 'react-query';
import axios from 'axios';

import { TeamMember } from '@/models/TeamMembers';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import { AwsConfigApiGateway } from '@/utils/config/api';

import queryKeys from '../../modules/shared/query/queryKeys';

/** Value stored as ticket assignee — prefer Cognito id so it matches JWT claims. */
export function assigneeOptionValue(member: TeamMember): string {
  return (member.cognitoUserId || member.id || member.email || '').trim();
}

export function findTeamMemberByAssigneeId(
  members: TeamMember[],
  assigneeId: string | undefined | null,
): TeamMember | undefined {
  if (assigneeId == null || assigneeId === '') return undefined;
  const a = assigneeId.trim();
  const lower = a.toLowerCase();
  return members.find((m) => {
    if (m.cognitoUserId && m.cognitoUserId === a) return true;
    if (m.id && m.id === a) return true;
    if (m.email && m.email.toLowerCase() === lower) return true;
    return false;
  });
}

function iso(d: unknown): string {
  if (typeof d === 'string' && d) return d;
  return new Date().toISOString();
}

/** Maps .NET `TeamMembersController.List` JSON to the shape the SPA expects. */
export function normalizeTeamMemberFromApi(raw: unknown): TeamMember {
  const m = raw as Record<string, unknown>;
  const email = String(m.email ?? '');
  const fn = String(m.firstName ?? m.givenName ?? '');
  const ln = String(m.lastName ?? m.familyName ?? '');
  const full = [fn, ln].filter(Boolean).join(' ').trim();
  const userName = full || (email ? email.split('@')[0] : '');

  return {
    id: m.id != null ? String(m.id) : '',
    cognitoUserId: String(m.cognitoUserId ?? ''),
    email,
    givenName: fn,
    familyName: ln,
    userName,
    created: iso(m.createdAt ?? m.created),
    modified: iso(m.updatedAt ?? m.modified),
    enabled: m.isDisabled !== true,
    status: String(m.status ?? 'CONFIRMED'),
    tenantId: String(m.tenantId ?? ''),
    userRole: String(m.role ?? m.userRole ?? ''),
    lastLogin: String(m.lastLogin ?? ''),
  };
}

function parseTeamMembersResponse(body: unknown): TeamMember[] {
  if (Array.isArray(body)) {
    return body.map(normalizeTeamMemberFromApi);
  }
  if (
    body &&
    typeof body === 'object' &&
    'data' in body &&
    Array.isArray((body as { data: unknown }).data)
  ) {
    return (body as { data: unknown[] }).data.map(normalizeTeamMemberFromApi);
  }
  return [];
}

async function getTeamMembers(token: string | undefined): Promise<TeamMember[]> {
  const response = await axios.get<unknown>(`${AwsConfigApiGateway.pooledTenantApiUrl}/team-members`, {
    headers: {
      'x-api-key': AwsConfigApiGateway.pooledTenantApiKey,
      Authorization: 'Bearer ' + token,
    },
  });
  return parseTeamMembersResponse(response.data);
}

export function useGetTeamMembers() {
  const { user } = useAuth();
  return useQuery<TeamMember[]>({
    queryKey: [queryKeys.teamMembers],
    queryFn: () => getTeamMembers(user?.signInUserSession?.idToken?.jwtToken),
    enabled: !!user?.signInUserSession?.idToken?.jwtToken,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}
